import {DragEvent, useRef, useState} from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {Box, Typography} from '@mui/material';

interface Props {
  file: File | null;
  label: string;
  dropzoneCta: string;
  acceptedFormatLabel: string;
  selectedFileLabel: string;
  error?: string;
  onFileSelect: (file: File | null) => void;
}

export const BatchInvitationsCsvUploadField = ({
  file,
  label,
  dropzoneCta,
  acceptedFormatLabel,
  selectedFileLabel,
  error,
  onFileSelect,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState<boolean>(false);

  const openFilePicker = () => {
    if (!fileInputRef.current) return;
    // Clear previous value so re-selecting the same file triggers onChange.
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileSelect(event.currentTarget.files?.[0] || null);
    // Keep input reusable for selecting the same filename again.
    event.currentTarget.value = '';
  };

  const handleDropFile = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    onFileSelect(event.dataTransfer.files?.[0] || null);
  };

  return (
    <>
      <Typography variant='h4' marginBottom='8px' marginTop='8px'>
        {label}
      </Typography>
      <Box
        onDrop={handleDropFile}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDraggingFile(true);
        }}
        onDragLeave={() => setIsDraggingFile(false)}
        onClick={openFilePicker}
        sx={{
          border: '1px dashed',
          borderColor: error ? 'error.main' : isDraggingFile ? 'primary.main' : 'divider',
          borderRadius: '8px',
          cursor: 'pointer',
          p: 3,
          textAlign: 'center',
          backgroundColor: isDraggingFile ? 'action.hover' : 'background.paper',
        }}
      >
        <input
          ref={fileInputRef}
          type='file'
          accept='.csv,text/csv'
          style={{display: 'none'}}
          onChange={handleFileInputChange}
        />
        <CloudUploadIcon color={error ? 'error' : 'action'} />
        <Typography variant='body2' marginTop={1}>
          {dropzoneCta}
        </Typography>
        <Typography variant='caption' color='text.secondary' display='block' marginTop={0.5}>
          {acceptedFormatLabel}
        </Typography>
        {file && (
          <Typography variant='body2' marginTop={1}>
            {selectedFileLabel}
          </Typography>
        )}
      </Box>
      {!!error && (
        <Typography variant='body2' color='error' marginTop={0.5}>
          {error}
        </Typography>
      )}
    </>
  );
};
